import { useAbility } from "@casl/react";
import { useState } from "react";

import type { AppAbility } from "@recobra/core";
import { m } from "@recobra/i18n/messages";
import { OPEN_TASK_STATUSES } from "@recobra/schemas/task";
import type { Task } from "@recobra/schemas/task";

import { useAuth } from "../../lib";
import { useUpdateTaskMutation } from "../task-data";
import { buildTaskFieldUpdate, dvcSearchDetailsErrors, taskToFormValues } from "../task-form";
import { useTaskForm } from "../task-form";

export type EditableTaskField = Parameters<typeof buildTaskFieldUpdate>[1];

export const useTaskMetadataEditor = (task: Task, executionOnly: boolean) => {
  const ability = useAbility<AppAbility>();
  const { user } = useAuth();
  const [editing, setEditing] = useState<EditableTaskField>();
  const canViewMetadata = ability.can("updateMetadata", "Task");
  const canEdit = !executionOnly && OPEN_TASK_STATUSES.includes(task.status) && canViewMetadata;
  if (!canEdit && editing !== undefined) setEditing(undefined);
  const mutation = useUpdateTaskMutation({
    username: user.username,
    contextType: task.contextType,
    contextId: task.contextId,
  });
  const form = useTaskForm({
    defaultValues: taskToFormValues(task),
    validators: {
      onSubmit: ({ value }) => {
        if (editing === "deadlineDate" && !value.deadlineDate) {
          return { fields: { deadlineDate: m.task_deadline_invalid() } };
        }
        if (editing !== "dvcSearchDetails") return undefined;
        const fields = dvcSearchDetailsErrors(value, task.system);

        return fields ? { fields } : undefined;
      },
    },
    onSubmit: ({ value }) => {
      if (!editing) return;
      mutation.mutate({ id: task.id, input: buildTaskFieldUpdate(value, editing) });
      setEditing(undefined);
    },
  });

  return {
    form,
    editing,
    isPending: mutation.isPending,
    canViewMetadata,
    canEdit,
    canEditField: (field: EditableTaskField) =>
      canEdit && (field !== "deadlineDate" || ability.can("manageDeadline", "Task")),
    startEditing: (field: EditableTaskField) => {
      form.reset(taskToFormValues(task));
      setEditing(field);
    },
    cancelEditing: () => setEditing(undefined),
    save: () => void form.handleSubmit(),
  };
};

export type TaskMetadataEditor = ReturnType<typeof useTaskMetadataEditor>;
